// src/pages/friends-page/components/friends-checklist-display-item/FriendsChecklistDisplayItem.js
import { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import FriendsCardInfoModal from '../friends-card-info-modal/FriendsCardInfoModal';
import { buildEbaySearchUrl as buildEbaySearchUrlUtil } from '../../../../utils/ebay/buildEbaySearchUrl';
import CardAttribute from '../../../../components/card-attribute/CardAttribute';

// styles
import {
    ChecklistItemDisplayContainer,
    CardDetails,
    TopCardContent,
    LeftCardContent,
    CardNumber,
    CardHeader,
    AttributesContainer,
    CardContentWrapper,
    FriendStatsContainer,
    FriendStat,
    FriendStatLabel,
    FriendStatValue
} from './FriendsChecklistDisplayItem.styles';

export default function FriendsChecklistDisplayItem({
    card,
    parallels = [],
    showcaseView = false,
    cardOpacity = 1,
    onCardSelect = null, // Optional callback for custom card selection (e.g., PSA)
    isSelected = false, // Whether this card is selected for batch operations
    onSelectionChange = null // Callback when selection changes: (cardNumber, isSelected) => void
}) {
    // Hooks
    const { sport, year, manufacturer, series, cardType, cardTypeCategory } = useParams();

    // State
    const [showCardInfoModal, setShowCardInfoModal] = useState(false);
    const [selectedParallel] = useState('BASE');
    const [qtyValue, setQtyValue] = useState(1);
    const [cardNumberInput, setCardNumberInput] = useState('');

    // Use ref to preserve expanded state when card data updates
    // Store expanded state in ref keyed by cardNumber so it persists across re-renders
    // This is necessary because Redux updates create new card object references
    const expandedStateRef = useRef({});

    const { name, cardAttributes, cardNumber, collectedCards } = card;

    // Initialize state from ref to preserve across re-renders
    const [variationsExpanded, setVariationsExpanded] = useState(() => {
        return expandedStateRef.current[cardNumber] || false;
    });

    // Use useLayoutEffect to synchronously restore expanded state before paint
    // This ensures the state is restored immediately when card data updates
    useLayoutEffect(() => {
        const persistedExpanded = expandedStateRef.current[cardNumber] || false;
        const hasCollectedCards = collectedCards?.length > 0;

        // If card was expanded and still has cards, restore expanded state immediately
        if (hasCollectedCards && persistedExpanded && !variationsExpanded) {
            setVariationsExpanded(true);
        } else if (!hasCollectedCards && variationsExpanded) {
            // If no cards left, collapse
            expandedStateRef.current[cardNumber] = false;
            setVariationsExpanded(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardNumber, collectedCards?.length]);

    // Sync state with ref when card data changes to preserve expanded state across Redux updates
    // This ensures that when the card object reference changes, we restore the expanded state
    useEffect(() => {
        const currentCollectedCardsLength = collectedCards?.length || 0;
        const wasExpanded = expandedStateRef.current[cardNumber] || false;

        // If card still has collectedCards and was previously expanded, restore expanded state
        if (currentCollectedCardsLength > 0 && wasExpanded && !variationsExpanded) {
            // Card was expanded and still has cards - restore expanded state
            setVariationsExpanded(true);
        } else if (currentCollectedCardsLength === 0 && variationsExpanded) {
            // If no cards left, collapse and clear the ref
            expandedStateRef.current[cardNumber] = false;
            setVariationsExpanded(false);
        } else if (currentCollectedCardsLength > 0 && wasExpanded !== variationsExpanded) {
            // Sync state with ref value (ref is source of truth)
            setVariationsExpanded(wasExpanded);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardNumber, collectedCards?.length]);

    // Keep ref in sync with state whenever state changes (state is source of truth for user actions)
    useEffect(() => {
        expandedStateRef.current[cardNumber] = variationsExpanded;
    }, [variationsExpanded, cardNumber]);

    // Reset card number input when parallel selection changes
    useEffect(() => {
        setCardNumberInput('');
    }, [selectedParallel]);

    const buildEbaySearchUrl = useCallback(
        (card, parallel) => buildEbaySearchUrlUtil(card, parallel, series),
        [series]
    );

    // Helper function to get max removable quantity for selected parallel
    const getMaxRemovableQty = useCallback(() => {
        if (selectedParallel === 'BASE') {
            const baseVariation = collectedCards?.find(c => c.cardParallelName === 'BASE');
            return baseVariation?.cardQty || 0;
        } else {
            const parallel = parallels.find(p => p.name === selectedParallel);
            if (!parallel) return 0;

            // If it's a numbered card and cardNumberInput is set, find matching variation
            if (parallel.limitedTo && cardNumberInput) {
                const matchingVariation = collectedCards?.find(
                    c => c.cardParallelName === selectedParallel && 
                         c.cardPrintNumber === parseInt(cardNumberInput, 10)
                );
                return matchingVariation?.cardQty || 0;
            } else {
                // For non-numbered cards, find any variation with this parallel name
                const variation = collectedCards?.find(c => c.cardParallelName === selectedParallel);
                return variation?.cardQty || 0;
            }
        }
    }, [selectedParallel, collectedCards, parallels, cardNumberInput]);

    // Reset qtyValue if it exceeds max removable quantity when parallel or card number changes
    useEffect(() => {
        const maxRemovable = getMaxRemovableQty();
        if (qtyValue < 0 && Math.abs(qtyValue) > maxRemovable) {
            // Clamp to max removable (as negative) or reset to 1 if nothing collected
            setQtyValue(maxRemovable > 0 ? -maxRemovable : 1);
        }
    }, [selectedParallel, cardNumberInput, collectedCards, qtyValue, getMaxRemovableQty]);


    const handleClick = () => {
        // Don't show card info modal in showcase view or when onCardSelect is provided (PSA selection)
        if (showcaseView || onCardSelect) {
            return;
        }
        // Show card info modal when clicking the main component
        setShowCardInfoModal(true);
    };

    // Set modal handlers
    // Checkbox selection handler
    const handleCheckboxChange = (e) => {
        e.stopPropagation();
        // Immediately blur to prevent scroll behavior from focus
        e.target.blur();
        if (onSelectionChange) {
            onSelectionChange(card.cardNumber, e.target.checked);
        }
    };

    // Removed handleAddMenuToggle - add menu is commented out

    // Render helpers
    const renderCardHeader = () => (
        <CardHeader>
            {name.map((playerName, index) => (
                <span key={index}>
                    <h3>{playerName}</h3>
                    {index < name.length - 1 && ' • '}
                </span>
            ))}
        </CardHeader>
    );

    const renderAttributes = () => (
        <AttributesContainer>
            {cardAttributes?.length > 0 && cardAttributes.map(attribute => (
                <CardAttribute key={attribute} text={attribute} />
            ))}
        </AttributesContainer>
    );

    const renderQuantityControls = () => {
        // Friend view: show read-only Total Quantity and Variations Collected (no dropdown, ticker, or add/remove)
        const totalQty = collectedCards?.reduce((sum, c) => sum + (c.cardQty || 0), 0) ?? 0;
        const variationsCount = collectedCards?.length ?? 0;

        return (
            <FriendStatsContainer>
                <FriendStat>
                    <FriendStatLabel>Total Qty</FriendStatLabel>
                    <FriendStatValue>{totalQty}</FriendStatValue>
                </FriendStat>
                <FriendStat>
                    <FriendStatLabel>Variations</FriendStatLabel>
                    <FriendStatValue>{variationsCount}</FriendStatValue>
                </FriendStat>
            </FriendStatsContainer>
        );
    };


    return (
        <ChecklistItemDisplayContainer
            $isCollected={collectedCards?.length > 0}
            onClick={handleClick}
            className='panel-bg dropshadow'
        >
            <TopCardContent>
                {/* Checkbox for batch selection - far left */}
                {onSelectionChange && (
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={handleCheckboxChange}
                        onClick={(e) => e.stopPropagation()}
                        tabIndex={-1}
                        style={{
                            width: '20px',
                            height: '20px',
                            cursor: 'pointer',
                            marginRight: '12px',
                            flexShrink: 0
                        }}
                    />
                )}
                <CardContentWrapper $opacity={cardOpacity}>
                    <LeftCardContent>
                        <CardNumber>
                            <h4>{cardNumber}</h4>
                        </CardNumber>
                        <CardDetails>
                            {renderCardHeader()}
                            {(cardAttributes?.length > 0 || collectedCards?.length > 0) && renderAttributes()}
                        </CardDetails>
                    </LeftCardContent>
                </CardContentWrapper>
                {renderQuantityControls()}
            </TopCardContent>

            {/* Expanded view - kept for later use */}
            {/* {renderVariations()} */}
            {/* {renderParallelConfiguration()} */}

            <FriendsCardInfoModal
                isOpen={showCardInfoModal}
                onClose={() => setShowCardInfoModal(false)}
                card={card}
                year={year}
                manufacturer={manufacturer}
                series={series}
                buildEbaySearchUrl={buildEbaySearchUrl}
                parallels={parallels}
                sport={sport}
                cardType={cardType}
                cardTypeCategory={cardTypeCategory}
                dispatchCardToQueue={() => {}}
                handleRemoveCard={() => {}}
                showcaseView={showcaseView}
            />
        </ChecklistItemDisplayContainer>
    );
}